/**
 * A PROJECT the crew member works directly (D-3/A3) on Today / My Week — the
 * counterpart of the task card, same visual grammar: state colour, "who you're
 * with", site, hours, and the two-button Start / Finish flow (D-10: Start puts
 * the project `on_site`, which the crew read as "In progress"; Finish →
 * `pending_closeout`, which they read as "Finished"; a mis-tapped Finish can be
 * undone within the grace window). Tapping the card opens the crew project
 * screen.
 */
import { format, parseISO } from 'date-fns';
import {
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Clock,
  MapPin,
  Play,
  RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import type { CrewTodayProject } from '@vari-crew/schemas';
import { isQueuedProjectResult, useTransitionProject } from '@/api/project-transitions';
import { Button } from '@/components/ui/Button';
import { CrewAvatars } from '@/components/ui/CrewAvatars';
import { cn } from '@/lib/cn';
import {
  CREW_PROJECT_ACTIVE,
  CREW_PROJECT_STATUS_LABEL,
  PROJECT_FINISH_UNDO_GRACE_MS,
  crewCanFinish,
  crewCanStart,
} from '@/lib/projectStatusCrew';

function timeOnly(iso: string | null): string {
  if (!iso) return '';
  return format(parseISO(iso), 'h:mma').toLowerCase();
}
function fmtDayWindow(w: { start_time: string; end_time: string }): string {
  const f = (hm: string) => {
    const [h = 0, m = 0] = hm.split(':').map(Number);
    const ap = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, '0')}${ap}`;
  };
  return `${f(w.start_time)} – ${f(w.end_time)}`;
}

export function CrewProjectCard({
  project,
  expanded,
  canAct,
  onOpen,
}: {
  project: CrewTodayProject;
  /** Full card with actions (Today) vs. compact row (My Week). */
  expanded: boolean;
  /** Crew/supervisor may Start/Finish; a manager viewing another crew member may not. */
  canAct: boolean;
  onOpen: () => void;
}) {
  const transition = useTransitionProject(project.project_id);
  const isActive = CREW_PROJECT_ACTIVE.has(project.status);
  const isFinished = project.status === 'pending_closeout' || project.status === 'complete';
  const isBlocked = project.status === 'blocked';
  const canUndo =
    project.status === 'pending_closeout' &&
    project.finished_at != null &&
    Date.now() - parseISO(project.finished_at).getTime() < PROJECT_FINISH_UNDO_GRACE_MS;

  const visualClass = isFinished
    ? 'bg-status-complete text-status-complete-fg border-status-complete-fg/20'
    : isActive
      ? 'bg-status-onsite text-status-onsite-fg border-status-onsite-fg/40 ring-1 ring-status-onsite-fg/30'
      : isBlocked
        ? 'bg-status-blocked text-status-blocked-fg border-status-blocked-fg/40'
        : 'bg-bg border-line';

  function go(to: 'on_site' | 'pending_closeout') {
    const said = to === 'on_site' ? 'in progress' : 'finished';
    transition.mutate(
      { to },
      {
        onSuccess: (res) =>
          isQueuedProjectResult(res)
            ? toast.success(`Saved offline — "${said}" will sync when you reconnect`)
            : toast.success(`Project marked ${said}`),
        onError: (err) => toast.error(`Failed: ${err.message}`),
      },
    );
  }

  const window =
    project.scheduled_start && project.scheduled_end
      ? `${timeOnly(project.scheduled_start)} – ${timeOnly(project.scheduled_end)}`
      : project.project_day_window
        ? fmtDayWindow(project.project_day_window)
        : null;

  if (!expanded) {
    return (
      <button
        onClick={onOpen}
        className={cn(
          'flex items-center gap-3 p-3 rounded-xl border text-sm w-full text-left',
          visualClass,
        )}
      >
        <Briefcase className="w-4 h-4 flex-shrink-0 opacity-70" />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">
            {isFinished && '✓ '}
            {isActive && '▶ '}
            {timeOnly(project.scheduled_start)} · {project.project_title}
          </div>
          <div className="text-xs opacity-75 truncate">
            Project · {CREW_PROJECT_STATUS_LABEL[project.status]}
            {project.is_lead && ' · you lead'}
          </div>
        </div>
        {project.crew.length > 0 && <CrewAvatars crew={project.crew} max={3} className="shrink-0" />}
        <ChevronRight className="w-4 h-4 opacity-60 flex-shrink-0" />
      </button>
    );
  }

  return (
    <div className={cn('p-4 rounded-2xl border', visualClass)}>
      <button onClick={onOpen} className="flex items-start gap-2.5 mb-1 w-full text-left">
        <Briefcase className="w-5 h-5 mt-0.5 flex-shrink-0 opacity-80" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-base leading-tight">
            {isActive && '▶ '}
            {timeOnly(project.scheduled_start)} · {project.project_title}
          </div>
          <div className="text-xs opacity-80 mt-0.5">
            Project · {CREW_PROJECT_STATUS_LABEL[project.status]}
            {project.is_lead && ' · you lead'}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 mt-1 opacity-50 flex-shrink-0" />
      </button>

      {project.crew.length > 0 && (
        <CrewAvatars crew={project.crew} withNames className="mt-2" nameClass="opacity-80" />
      )}
      {project.site_short_address && (
        <div className="flex items-center gap-1.5 text-xs opacity-80 mt-2">
          <MapPin className="w-3 h-3" /> {project.site_short_address}
        </div>
      )}
      {window && (
        <div className="flex items-center gap-1.5 text-xs opacity-80 mt-1.5">
          <Clock className="w-3 h-3" /> Hours today · {window}
        </div>
      )}

      {/* h-11 (44px): glove/sunlight floor for the field's primary controls. */}
      {canAct && (crewCanStart(project.status) || crewCanFinish(project.status) || canUndo) && (
        <div className="flex gap-2 mt-4">
          {crewCanStart(project.status) && (
            <Button size="sm" className="flex-1 h-11" onClick={() => go('on_site')} disabled={transition.isPending}>
              <Play className="w-3.5 h-3.5" /> Start
            </Button>
          )}
          {crewCanFinish(project.status) && (
            <Button size="sm" className="flex-1 h-11" onClick={() => go('pending_closeout')} disabled={transition.isPending}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Finish
            </Button>
          )}
          {canUndo && (
            <Button size="sm" variant="outline" className="flex-1 h-11" onClick={() => go('on_site')} disabled={transition.isPending}>
              <RotateCcw className="w-3.5 h-3.5" /> Undo finish
            </Button>
          )}
        </div>
      )}

      <button
        onClick={onOpen}
        className="flex items-center justify-between w-full mt-3 pt-3 border-t border-current/10 text-xs opacity-75 hover:opacity-100"
      >
        <span>Open project · {project.project_client_name}</span>
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
